<template>
  <div>
    <v-menu
      :close-on-content-click="false"
      open-on-hover
      bottom
      :right="left ? undefined : true"
      :left="left ? true : undefined"
      :nudge-right="left ? '-12px' : '12px'"
      close-delay="150"
      open-delay="150"
      offset-x
      min-width="308px"
      max-width="308px"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="d-flex align-center">
          <loader
            v-on="on"
            v-bind="attrs"
            v-if="loading"
            :noMargins="true"
            :size="16"
            color="primary"
          />
          <v-icon
            v-else
            v-on="on"
            v-bind="attrs"
            size="16"
            style="line-height: 0px"
            :color="icon.color"
          >
            {{ `$${icon.type}` }}
          </v-icon>
        </div>
      </template>
      <div class="pa-4" v-if="!resolvedCorrectly">
        <div class="d-flex">
          <v-icon v-if="!dnsAlertsEnabled" color="gray" size="20">
            $okcircle
          </v-icon>
          <v-icon v-else color="error" size="20"> $alertcircle</v-icon>
          <p
            v-if="!dnsAlertsEnabled"
            class="gray--text text--darken-1 px-2 mb-2 p-xs"
          >
            {{ $t("heading.instance.dnsTooltip.alertsIgnored") }}
          </p>
          <p v-else class="error--text px-2 mb-2 p-xs">
            {{ $t("heading.instance.dnsTooltip.desc") }}
          </p>
        </div>
        <div class="d-flex">
          <v-btn
            v-if="dnsAlertsEnabled"
            color="error"
            class="v-btn--fix-dns ml-7 px-3"
            small
            @click="openDnsModal()"
          >
            <v-icon class="mr-1" color="white" size="20"> $wrenchRight</v-icon>
            <span class="">{{ $t("heading.instance.dnsTooltip.button") }}</span>
          </v-btn>

          <v-btn
            v-if="dnsAlertsEnabled"
            :loading="togglingDnsAlerts"
            color="gray"
            class="v-btn--fix-dns ml-7 px-3"
            small
            @click="toggleDnsAlerts(false)"
          >
            <span class="">{{ $t("button.ignore") }}</span>
          </v-btn>
          <v-btn
            v-else
            :loading="togglingDnsAlerts"
            color="gray"
            class="v-btn--fix-dns ml-7 px-3 mt-7"
            small
            @click="toggleDnsAlerts(true)"
          >
            <span class="">{{ $t("button.restoreAlerts") }}</span>
          </v-btn>
        </div>
      </div>
      <div class="pa-5" v-else>
        <div class="d-flex">
          <v-icon color="error" size="20"> $alertcircle</v-icon>
          <p class="error--text px-2 mb-2 p-xs">
            {{ sslInfo.text }}
          </p>
        </div>
        <p
          v-if="ssl.certificate && ssl.certificate.ip_address"
          class="gray--text text--darken-1 mb-2 p-xs"
        >
          {{ $t("tooltip.dnsIndicator.detectedFrom", { ip: ssl.certificate.ip_address }) }}
        </p>
        <p
          v-if="ssl.certificate && ssl.certificate.points_to_server === false"
          class="warning--text mb-0 p-xs"
        >
          {{ $t("tooltip.dnsIndicator.resolvesElsewhere", { ip: ssl.certificate.ip_address }) }}
        </p>
      </div>
    </v-menu>
    <v-dialog
      ref="dialog"
      v-model="isOpenDnsModal"
      transition="custom-dialog-transition"
    >
      <div class="card-overlay" @click="openDnsModal()"></div>
      <v-card style="width: 560px">
        <v-card-title class="pb-6 flex justify-space-between align-center">
          <h4 class="font-weight-light">
            {{ $t("heading.instance.modal.setDomain.title") }}
          </h4>
          <v-btn icon x-small @click="openDnsModal()">
            <v-icon size="24">$close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pb-8">
          <div class="domain-container">
            <span class="p-3">
              {{ $t("heading.instance.modal.setDomain.config.subtitle") }}
              <span class="text--heading font-weight-600">
                {{ instance.domain }}
              </span>
            </span>
          </div>
          <div v-if="isAppSingleServer" class="domain-container">
            <span class="p-3">
              {{ $t("heading.instance.modal.setDomain.previewSite") }}
            </span>
            <v-btn
              color="primary"
              x-small
              elevation="0"
              @click="
                openDnsModal();
                previewSiteModelOpen = true;
              "
            >
              {{ $t("heading.instance.modal.setDomain.generateUrl") }}
            </v-btn>
            <PreviewSiteModal
              :is-open.sync="previewSiteModelOpen"
              :instance="instance"
              @close="previewSiteModelOpen = false"
            />
          </div>
          <DnsOptions
            :domain="instance.domain"
            :onboardingSubdomain="instance.onboarding_subdomain"
            :dnsSettings="instance.dns_settings"
            :ipv4Addresses="instance.getDnsIpv4Addresses()"
            :hasDnsServer="instance.service_has_dns"
            :nameservers="instance.dns_nameservers"
            :hostCNames="instance.host_c_names ?? []"
            :cdnDnsRecords="instance.cdn_dns_records || []"
            :cdnPending="cdnDnsPending(instance)"
            :dnsServerRecords="
              instance.domain_zone_details?.configuration_records ?? []
            "
          />
        </v-card-text>
        <v-card-actions class="d-flex flex-column pb-4 px-8 pt-0">
          <v-btn
            x-large
            elevation="0"
            color="primary"
            style="color: var(--v-white-base)"
            block
            @click="openDnsModal()"
          >
            {{ $t("button.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/apis/Api";
import {
  cdnDnsPending,
  skipARecordVerification,
} from "../../apis/CdnDns";
import Loader from "../Loader.vue";
import DnsOptions from "../DnsOptions.vue";
import PreviewSiteModal from '../modal/PreviewSiteModal.vue';
import moment from "moment";

export default {
  components: {
    Loader,
    DnsOptions,
    PreviewSiteModal,
  },
  props: {
    instance: Object,
    hasText: Boolean,
    left: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      togglingDnsAlerts: false,
      isOpenDnsModal: false,
      loading: true,
      resolvedCorrectly: true,
      btnDisabled: false,
      throttleTime: 5000,
      previewSiteModelOpen: false,
    };
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    dnsAlertsEnabled: {
      get() {
        return this.instance.details.dns_alerts_enabled;
      },
      set(value) {
        this.instance.setDnsAlertsEnabled(value);
      },
    },
    ssl() {
      if (
        !this.instance.domain_ssl_details ||
        !this.instance.domain_ssl_details.detected_ssl_cert ||
        !this.instance.domain_ssl_details.detected_ssl_cert.not_before
      ) {
        return {
          certificate_installed: false,
          certificate: null,
        };
      }
      return {
        certificate_installed: true,
        certificate: this.instance.domain_ssl_details.detected_ssl_cert,
      };
    },
    sslInfo() {
      if (this.loading) {
        return {
          text: this.$t("tooltip.dnsIndicator.fetching", {
            url: this.instance.url,
          }),
        };
      }
      if (!this.ssl.certificate_installed) {
        return { text: this.$t("tooltip.dnsIndicator.notSsl") };
      }
      if (this.ssl.certificate && this.ssl.certificate.self_signed) {
        return {
          text: this.$t("tooltip.dnsIndicator.selfSigned"),
        };
      }
      if (this.ssl.certificate && !this.ssl.certificate.name_match) {
        return {
          text: this.$t("tooltip.dnsIndicator.sslNotMatched"),
        };
      }
      return { text: this.$t("tooltip.dnsIndicator.secure") };
    },
    icon() {
      if (!this.resolvedCorrectly) {
        if (!this.dnsAlertsEnabled) {
          return { type: "okcircle", color: "gray" };
        }
        return { type: "alertcircle", color: "warning" };
      }
      if (!this.ssl.certificate_installed) {
        return { type: "unlock", color: "error" };
      }
      if (this.ssl.certificate && this.ssl.certificate.self_signed) {
        return { type: "lock", color: "error" };
      }
      if (this.ssl.certificate && !this.ssl.certificate.name_match) {
        return { type: "unlock", color: "error" };
      }
      return { type: "lock", color: "success" };
    },
    instanceAge() {
      return moment().diff(moment(this.instance.created_at)) / 1000 / 3600;
    },
  },
  mounted() {
    if (!this.instance.host_ip_address) {
      this.loading = false;
      this.resolvedCorrectly = false;
      return;
    }
    this.getInfo();
  },
  methods: {
    cdnDnsPending,
    openDnsModal() {
      this.isOpenDnsModal = !this.isOpenDnsModal;
    },
    toggleDnsAlerts(value) {
      this.togglingDnsAlerts = true;
      let endpoint = `/instances/${this.instance.id}/dns/alerts/`;
      endpoint += value ? "enable" : "disable";
      Api.put(endpoint)
        .then((response) => {
          this.dnsAlertsEnabled = response.data.data;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.togglingDnsAlerts = false;
        });
    },
    async getInfo() {
      if (!this.instance.isActive) return;

      await this.refreshSsl();
      if (this.ssl.certificate_installed === false) {
        await this.checkDnsPropagation();
      }
      this.loading = false;
    },
    async refreshSsl() {
      if (this.instance.domain_ssl_details === null) {
        return;
      }
      if (this.instanceAge < 1) {
        await this.getDetectedSslCertificate();
        return;
      }
      let certMaxCacheMinutes = 2880;
      if (this.instanceAge < 24) {
        certMaxCacheMinutes = 5;
      }
      let now = moment();
      let detected = moment.unix(
        this.instance.domain_ssl_details?.detected_ssl_cert_checked_time ?? null
      );
      if (now.diff(detected) / 1000 / 60 > certMaxCacheMinutes) {
        await this.getDetectedSslCertificate();
      }
    },
    async getDetectedSslCertificate() {
      await Api.hosting(this.instance.hostingId)
        .getDetectedCertificate(this.instance.domain, true)
        .then((response) => {
          this.instance.setDetectedSslCert(response);
        })
        .catch((/*error*/) => {
          // this.$store.dispatch("addAlert", {
          //   success: false,
          //   errorMessage: Api.getErrorMessage(error),
          // });
        });
    },
    async checkDnsPropagation() {
      if (this.instance.hasIpAddressInUrl()) {
        return true;
      }
      this.resolvedCorrectly = await Api.dns().verifyDomainResolving(
        this.instance.domain,
        this.instance.onboarding_subdomain,
        this.instance.dns_settings,
        this.instance.service_has_dns,
        this.instance.getDnsIpv4Addresses(),
        this.instance.dns_nameservers,
        this.instance.host_c_names ?? [],
        this.instance.domain_zone_details?.configuration_records ?? [],
        false,
        skipARecordVerification(this.instance)
      );
      this.instance.setDnsResolveStatus(this.resolvedCorrectly);
      if (!this.resolvedCorrectly) {
        this.$emit("dns-propagation-in-progress");
      }
    },
  },
  watch: {
    isOpenDnsModal: function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
};
</script>

<style lang="scss" scoped>
.domain-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 6px;
  border: 1px solid var(--v-gray-lighten2);
  padding: 16px 16px;
  margin-bottom: 16px;
}
.v-btn.v-btn--fix-dns {
  box-shadow: none;
}
.dns-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: transparent;
  position: relative;

  &:before {
    position: absolute;
    content: "";
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: var(--v-gray-lighten3);
    top: calc(50% - 7px);
    left: calc(50% - 7px);
    border: 3px solid var(--v-gray-lighten5);
  }

  &--online {
    &:before {
      background-color: var(--v-primary-base);
      border: 3px solid var(--v-primary-lighten5);
    }
  }

  &--offline {
    &:before {
      background-color: var(--v-error-base);
      border: 3px solid var(--v-error-lighten5);
    }
  }
}
.nameserver-list {
  padding: 0px;
  list-style: none;

  .list-item {
    position: relative;
    padding-left: 14px;
    font-weight: 400;
    font-size: var(--font-size-medium);
    display: flex;
    line-height: 25px;
    color: var(--v-gray-darken4);

    + .list-item {
      margin-top: 4px;
    }

    &::before {
      content: "";
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 100%;
      left: 0px;
      top: calc(50% - 3px);
      background: var(--v-primary-base);
    }
  }

  &--error {
    .list-item {
      &::before {
        background: var(--v-error-base);
      }
    }
  }
}

.list-container {
  + .list-container {
    margin-top: 16px;
  }

  &__title {
    display: inline-block;
    font-weight: 700;
    line-height: 26px;
    font-size: var(--font-size-base);
    color: var(--v-gray-darken4);
    margin-bottom: 8px;
  }
}
</style>
